import React, { useState, useEffect, useCallback } from 'react';
import {
  getLedger, deleteLedgerEntry,
  getChangeOrders, updateChangeOrder, deleteChangeOrder,
  joinWaitlist, getProfiles,
} from '../api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../App';
import {
  BookOpen, ClipboardList, TrendingUp, Lock, Plus, Check, Loader,
  ChevronDown, ChevronUp, Download, FileSignature, Trash2, DollarSign,
} from './Icons';

import ChangeOrderPanel from './ChangeOrderPanel';
import './ScopeLedger.css';

// Legacy ledger entry status config (kept for reference)
// const STATUS_CONFIG = { ... };
// const VALID_STATUSES = [...];
const CO_STATUS_CONFIG = {
  draft:    { label: 'Draft',    color: '#6b7280', bg: 'rgba(107,114,128,0.1)'  },
  sent:     { label: 'Sent',     color: '#f59e0b', bg: 'rgba(245,158,11,0.1)'   },
  approved: { label: 'Approved', color: '#22c55e', bg: 'rgba(34,197,94,0.1)'    },
  declined: { label: 'Declined', color: '#ef4444', bg: 'rgba(239,68,68,0.1)'    },
};
const VALID_CO_STATUSES = ['draft', 'sent', 'approved', 'declined'];

const SCOPE_PREVIEW_COUNT = 5;

function fmtMoney(amount, currency = 'USD') {
  if (amount == null || isNaN(amount)) return null;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 2 }).format(amount);
}

function coTotal(co) {
  return co.pricing_type === 'hourly'
    ? (co.hours || 0) * (co.rate || 0)
    : (co.price || 0);
}

// ── Locked state ───────────────────────────────────────────────────────────────
function LockedLedger() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handleUpgrade() {
    setSubmitting(true);
    try { await joinWaitlist({ email: user?.email, plan: 'pro', source: 'scope-ledger' }); }
    catch { /* silently succeed */ }
    finally {
      setSubmitting(false);
      setShowModal(false);
      addToast(`Got it! We'll reach out to ${user?.email} to complete your upgrade.`, 'success');
    }
  }

  return (
    <>
      <div className="sl-locked-card">
        <Lock size={22} className="sl-locked-icon" />
        <div className="sl-locked-content">
          <div className="sl-locked-title">Scope Ledger & Change Orders — Pro Feature</div>
          <p className="sl-locked-desc">
            Auto-log out-of-scope requests, build branded change order PDFs, and track every billable addition from agreed to approved.
          </p>
        </div>
        <button className="btn btn-primary sl-upgrade-btn" onClick={() => setShowModal(true)}>
          Upgrade to Pro
        </button>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-panel" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <h2>Unlock Scope Ledger</h2>
              <button className="modal-close-btn" onClick={() => setShowModal(false)}>&#x2715;</button>
            </div>
            <div style={{ padding: '4px 0 16px' }}>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: 14 }}>
                Pro gives you the Scope Ledger, branded Change Order PDFs, unlimited checks, and 5 active projects.
              </p>
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 14px' }}>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Google account</div>
                <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>{user?.email}</div>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleUpgrade} disabled={submitting}>
                {submitting ? 'Sending…' : 'Request Upgrade → Pro'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Agreed Scope Tab ────────────────────────────────────────────────────────────
function AgreedScopeTab({ items, hasScope }) {
  const [expanded, setExpanded] = useState(false);

  if (!hasScope || items.length === 0) {
    return <div className="sl-empty">Upload a PDF to see your agreed scope baseline here.</div>;
  }

  const visible = expanded ? items : items.slice(0, SCOPE_PREVIEW_COUNT);
  const hiddenCount = items.length - SCOPE_PREVIEW_COUNT;

  return (
    <div>
      <ol className="sl-scope-list">
        {visible.map((item, i) => {
          const text = typeof item === 'object' ? item.text : item;
          return <li key={i} className="sl-scope-item">{text}</li>;
        })}
      </ol>
      {items.length > SCOPE_PREVIEW_COUNT && (
        <button className="sl-show-more-btn" onClick={() => setExpanded(v => !v)}>
          {expanded
            ? <><ChevronUp size={13} /> Show less</>
            : <><ChevronDown size={13} /> Show {hiddenCount} more deliverables</>
          }
        </button>
      )}
    </div>
  );
}

// ── Change Orders Tab ──────────────────────────────────────────────────────────
function ChangeOrdersTab({ entries, changeOrders, project, branding, onUpdate, onDeleteEntry, onCOUpdate, onDeleteCO, onOpenPanel }) {
  const { addToast } = useToast();
  const currency = branding?.currency || 'USD';

  const approvedTotal = changeOrders
    .filter(co => co.status === 'approved')
    .reduce((s, co) => s + coTotal(co), 0);

  async function handleCOStatusChange(coId, newStatus) {
    try {
      const updated = await updateChangeOrder(coId, { status: newStatus });
      onCOUpdate(updated);
    } catch {
      addToast('Failed to update change order', 'error');
    }
  }

  async function handleDeleteCO(coId) {
    try {
      await deleteChangeOrder(coId);
      onDeleteCO(coId);
      addToast('Change order deleted', 'success');
    } catch {
      addToast('Failed to delete change order', 'error');
    }
  }

  async function handleDownload(co) {
    const { generateChangeOrderPDF } = await import('./ChangeOrderPDF');
    try {
      await generateChangeOrderPDF({ co, allCOs: changeOrders, project, branding });
    } catch {
      addToast('PDF generation failed', 'error');
    }
  }

  return (
    <div>
      <div className="sl-tab-action-row">
        {approvedTotal > 0 && (
          <div className="sl-co-approved-total">
            <DollarSign size={13} />
            {fmtMoney(approvedTotal, currency)} approved
          </div>
        )}
        <button className="btn btn-primary sl-add-btn" onClick={() => onOpenPanel(null)}>
          <Plus size={13} /> New Change Order
        </button>
      </div>

      {changeOrders.length === 0 ? (
        <div className="sl-empty sl-co-empty">
          <FileSignature size={28} className="sl-co-empty-icon" />
          <div>No change orders yet.</div>
          <p>Convert an out-of-scope request from the Unbilled Drift tab, or create one manually.</p>
        </div>
      ) : (
        <div className="sl-co-list">
          {changeOrders.map(co => {
            const cfg = CO_STATUS_CONFIG[co.status] || CO_STATUS_CONFIG.draft;
            const amt = coTotal(co);
            return (
              <div key={co.id} className="sl-co-card">
                <div className="sl-co-card-left">
                  <div className="sl-co-num">CO-{String(co.co_number).padStart(3,'0')}</div>
                  <div className="sl-co-body">
                    <div className="sl-co-title">{co.title}</div>
                    <div className="sl-co-meta">
                      {co.client_name && <span>{co.client_name}</span>}
                      {co.client_company && <span>{co.client_company}</span>}
                      <span>{new Date(co.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    </div>
                    {co.payment_terms && <div className="sl-co-terms">{co.payment_terms}</div>}
                  </div>
                </div>
                <div className="sl-co-card-right">
                  {amt > 0 && (
                    <div className="sl-co-amount">{fmtMoney(amt, currency)}</div>
                  )}
                  <div className="sl-co-status-row">
                    <select
                      className="sl-co-status-select"
                      value={co.status}
                      onChange={e => handleCOStatusChange(co.id, e.target.value)}
                      style={{ color: cfg.color }}
                    >
                      {VALID_CO_STATUSES.map(s => (
                        <option key={s} value={s}>{CO_STATUS_CONFIG[s].label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="sl-co-actions">
                    <button
                      className="sl-co-action-btn sl-co-download"
                      onClick={() => handleDownload(co)}
                      title="Download PDF"
                    >
                      <Download size={13} />
                    </button>
                    <button
                      className="sl-co-action-btn sl-co-delete"
                      onClick={() => handleDeleteCO(co.id)}
                      title="Delete"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Unbilled Drift Tab ─────────────────────────────────────────────────────────
function UnbilledDriftTab({ entries, onConvertToCO, onDelete }) {
  if (entries.length === 0) {
    return <div className="sl-empty">No flagged requests yet. Out-of-scope checks auto-log here.</div>;
  }

  return (
    <div className="sl-entries">
      {entries.map(entry => (
        <div key={entry.id} className="sl-entry sl-entry-flagged">
          <div className="sl-entry-body">
            <div className="sl-entry-desc">{entry.description}</div>
            {entry.client_request && entry.client_request !== entry.description && (
              <div className="sl-entry-request">
                &ldquo;{entry.client_request.slice(0, 200)}{entry.client_request.length > 200 ? '…' : ''}&rdquo;
              </div>
            )}
            {entry.sow_clause && <div className="sl-entry-clause">§ {entry.sow_clause}</div>}
            <div className="sl-entry-date">{new Date(entry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
          </div>
          <div className="sl-entry-controls sl-entry-controls--drift">
            <button
              className="btn btn-primary sl-convert-btn"
              onClick={() => onConvertToCO(entry)}
              title="Convert to Change Order"
            >
              <FileSignature size={12} /> Convert to CO
            </button>
            <button className="sl-delete-btn" onClick={() => onDelete(entry.id)} title="Dismiss">✕</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Savings Banner ─────────────────────────────────────────────────────────────
function SavingsBanner({ changeOrders, flaggedCount, profile }) {
  const currency = profile?.currency || 'USD';
  const approvedTotal = changeOrders
    .filter(co => co.status === 'approved')
    .reduce((s, co) => s + coTotal(co), 0);
  const pendingTotal = changeOrders
    .filter(co => co.status !== 'approved' && co.status !== 'declined')
    .reduce((s, co) => s + coTotal(co), 0);
  if (approvedTotal === 0 && flaggedCount === 0) return null;

  return (
    <div className="sl-savings-banner">
      <div className="sl-savings-item sl-savings-green">
        <div className="sl-savings-val">{fmtMoney(approvedTotal, currency)}</div>
        <div className="sl-savings-label">Billed via change orders</div>
      </div>
      {pendingTotal > 0 && (
        <div className="sl-savings-item sl-savings-yellow">
          <div className="sl-savings-val">{fmtMoney(pendingTotal, currency)}</div>
          <div className="sl-savings-label">Pending approval</div>
        </div>
      )}
      {flaggedCount > 0 && (
        <div className="sl-savings-item sl-savings-red">
          <div className="sl-savings-val">{flaggedCount}</div>
          <div className="sl-savings-label">Flagged requests to bill</div>
        </div>
      )}
    </div>
  );
}

// ── Main ScopeLedger ───────────────────────────────────────────────────────────
export default function ScopeLedger({ project, hasScope }) {
  const { planMeta } = useAuth();
  const { addToast } = useToast();

  const hasLedger = planMeta?.ledger === true;
  const projectId = project?.id;

  const [entries, setEntries] = useState([]);
  const [changeOrders, setChangeOrders] = useState([]);
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('orders');

  // Panel state
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelPrefill, setPanelPrefill] = useState(null);

  const scopeKnowledge = project?.scopeKnowledge || {};
  const agreedScope = [...(scopeKnowledge.deliverables || [])].filter(Boolean);
  const flagged = entries.filter(e => e.type === 'flagged_request');

  const load = useCallback(async () => {
    if (!hasLedger || !projectId) return;
    setLoading(true);
    try {
      const [ledgerData, cosData, profileData] = await Promise.all([
        getLedger(projectId),
        getChangeOrders(projectId),
        getProfiles(),
      ]);
      setEntries(Array.isArray(ledgerData) ? ledgerData : []);
      setChangeOrders(Array.isArray(cosData) ? cosData : []);
      setBranding(profileData?.[0] || null);
    } catch {
      addToast('Could not load ledger data', 'error');
    } finally {
      setLoading(false);
    }
  }, [hasLedger, projectId, addToast]);

  useEffect(() => { load(); }, [load]);

  function openPanel(prefill) {
    setPanelPrefill(prefill);
    setPanelOpen(true);
  }

  function handleConvertToCO(entry) {
    openPanel({
      ledger_entry_id: entry.id,
      title: entry.description,
      description: entry.client_request || '',
      client_name: project?.clientName || '',
    });
  }

  async function handleDeleteEntry(entryId) {
    try {
      await deleteLedgerEntry(entryId);
      setEntries(prev => prev.filter(e => e.id !== entryId));
    } catch {
      addToast('Failed to delete entry', 'error');
    }
  }

  if (!hasLedger) return (
    <div className="scope-ledger">
      <div className="sl-header">
        <BookOpen size={16} />
        <span className="sl-header-title">Scope Ledger</span>
        <span className="sl-pro-badge">Pro</span>
      </div>
      <div className="sl-locked-wrap"><LockedLedger /></div>
    </div>
  );

  const TABS = [
    { key: 'orders', label: 'Change Orders', icon: <ClipboardList size={13} />, count: changeOrders.length },
    { key: 'drift',  label: 'Unbilled Drift', icon: <TrendingUp size={13} />,    count: flagged.length, warn: flagged.length > 0 },
    { key: 'scope',  label: 'Agreed Scope',   icon: <Check size={13} />,          count: agreedScope.length },
  ];

  return (
    <>
      <div className="scope-ledger">
        {/* Header */}
        <div className="sl-header">
          <BookOpen size={16} />
          <span className="sl-header-title">Scope Ledger</span>
          {loading && <Loader size={13} className="spin sl-loading-inline" />}
          <span className="sl-header-desc">Living record of this project</span>
        </div>

        {/* Savings Banner */}
        <SavingsBanner
          changeOrders={changeOrders}
          flaggedCount={flagged.length}
          profile={branding}
        />

        {/* Tabs */}
        <div className="sl-tabs" role="tablist">
          {TABS.map(tab => (
            <button
              key={tab.key}
              role="tab"
              aria-selected={activeTab === tab.key}
              className={`sl-tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.icon}
              <span className="sl-tab-label">{tab.label}</span>
              {tab.count > 0 && (
                <span className={`sl-tab-count ${tab.warn ? 'warn' : ''}`}>{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="sl-tab-content">
          {activeTab === 'scope' && (
            <AgreedScopeTab items={agreedScope} hasScope={hasScope} />
          )}
          {activeTab === 'orders' && (
            <ChangeOrdersTab
              entries={entries.filter(e => e.type === 'change_order')}
              changeOrders={changeOrders}
              project={project}
              branding={branding}
              onUpdate={updated => setEntries(prev => prev.map(e => e.id === updated.id ? updated : e))}
              onDeleteEntry={handleDeleteEntry}
              onCOUpdate={updated => setChangeOrders(prev => prev.map(c => c.id === updated.id ? updated : c))}
              onDeleteCO={id => setChangeOrders(prev => prev.filter(c => c.id !== id))}
              onOpenPanel={openPanel}
            />
          )}
          {activeTab === 'drift' && (
            <UnbilledDriftTab
              entries={flagged}
              onConvertToCO={handleConvertToCO}
              onDelete={handleDeleteEntry}
            />
          )}
        </div>
      </div>

      {/* Change Order Panel */}
      {panelOpen && (
        <ChangeOrderPanel
          project={project}
          allCOs={changeOrders}
          prefill={panelPrefill}
          branding={branding}
          onCreated={co => {
            setChangeOrders(prev => [...prev, co]);
            // If came from a flagged entry, update it to change_order type
            if (panelPrefill?.ledger_entry_id) {
              setEntries(prev => prev.map(e =>
                e.id === panelPrefill.ledger_entry_id
                  ? { ...e, type: 'change_order', status: 'drafted' }
                  : e
              ));
            }
          }}
          onClose={() => { setPanelOpen(false); setPanelPrefill(null); }}
        />
      )}
    </>
  );
}
